import type {ComponentStageData, ReadOnlyExtensionAPI} from '../../types'
import {getMeasurementsPointer, getStructurePointer, getTargetLayoutPointer} from '../../conversionDal/utils/pointer'
import {isHeaderContainer, updateTargetLayout} from '../common'
import {generateChildLayout, shapeRules, sortHeaderContainerChildren} from './common'

export const createOrderRealStructure =
    (readOnlyExtensionAPI: ReadOnlyExtensionAPI) =>
    ({componentPointer, conversionDal, initialSnapshot, config}: ComponentStageData) => {
        const component = conversionDal.get(componentPointer)
        if (!component?.components || component?.type !== 'Container') {
            return
        }

        let startRow = 1

        conversionDal.modify(componentPointer, value => {
            const components = [...value.components].sort((childIdA, childIdB) => {
                const childALayout = initialSnapshot.getValue(getTargetLayoutPointer({id: childIdA}))
                const childBLayout = initialSnapshot.getValue(getTargetLayoutPointer({id: childIdB}))

                if (childALayout && childBLayout) {
                    return childALayout.itemLayout.gridArea.rowStart - childBLayout.itemLayout.gridArea.rowStart
                }

                if (childALayout) {
                    return -1
                }

                if (childBLayout) {
                    return 1
                }

                return shapeRules[config.requiredOrder](
                    conversionDal.get(getMeasurementsPointer({id: childIdA})),
                    conversionDal.get(getMeasurementsPointer({id: childIdB})),
                    readOnlyExtensionAPI.isRTL()
                )
            })

            for (const childId of components) {
                const childPointer = getStructurePointer({id: childId})
                const layout = initialSnapshot.getValue(getTargetLayoutPointer(childPointer))

                if (layout) {
                    startRow = Math.max(startRow, layout.itemLayout.gridArea.rowEnd)
                    continue
                }

                updateTargetLayout({conversionDal, componentPointer: childPointer}, generateChildLayout(startRow))
                startRow++
            }

            const isContainerInsideHeader = isHeaderContainer({componentPointer, conversionDal})
            if (isContainerInsideHeader) {
                components.sort(sortHeaderContainerChildren(conversionDal))
            }

            return {
                ...value,
                components
            }
        })

        const {containerLayout} = conversionDal.get(getTargetLayoutPointer(componentPointer)) ?? {}
        const currentRows = containerLayout.rows ?? []
        const rows =
            startRow > currentRows.length
                ? [...currentRows, ...Array(startRow - currentRows.length).fill({type: 'minContent'})]
                : [...currentRows].splice(-1, currentRows.length - startRow)
        updateTargetLayout(
            {conversionDal, componentPointer},
            {
                containerLayout: {
                    ...containerLayout,
                    rows,
                    type: 'GridContainerLayout'
                }
            }
        )

        return {appliedHeuristics: ['Z ORDER']}
    }
